import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Coffee } from "lucide-react";
import { api } from "@/lib/api";

/**
 * `/tip/thank-you?session_id=…` — polls /api/payments/status/{id} until the
 * payment flips to `paid`, then shows a warm confirmation.
 */
export default function TipThankYou() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | pending | error
  const [amount, setAmount] = useState(null);
  const [currency, setCurrency] = useState("usd");

  useEffect(() => {
    if (!sid) { setStatus("error"); return; }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (cancelled) return;
        setAmount(r.data?.amount);
        setCurrency(r.data?.currency || "usd");
        if (r.data?.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (attempts >= 20) { // ~20 * 2s = 40s
          setStatus("pending");
          return;
        }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sid]);

  const dollars = amount != null
    ? (amount / 100).toLocaleString(undefined, { style: "currency", currency: currency.toUpperCase() })
    : null;

  return (
    <div className="min-h-[60vh] grid place-items-center px-4 py-12" data-testid="tip-thank-you-page">
      <div className="max-w-md text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto mb-4 text-amber-600 animate-spin" />
            <h1 className="font-heading text-xl font-semibold text-slate-900 dark:text-slate-100">Confirming your tip…</h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-2">One moment while we hear back from Stripe.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 dark:bg-amber-950/40 grid place-items-center">
              <Coffee className="w-7 h-7 text-amber-700 dark:text-amber-300" data-testid="tip-thank-you-icon" />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-slate-900 dark:text-slate-100" data-testid="tip-thank-you-heading">
              Thank you!
            </h1>
            <p className="text-slate-600 dark:text-slate-300 mt-2 leading-relaxed" data-testid="tip-thank-you-body">
              Your{dollars ? ` ${dollars} ` : " "}tip means a lot. It quietly keeps this site running for other clinicians and families.
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Link to="/" className="text-sm text-sky-700 dark:text-sky-300 hover:underline">← Back to the site</Link>
            </div>
          </>
        )}
        {status === "pending" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-slate-400 dark:text-slate-500" />
            <h1 className="font-heading text-xl font-semibold text-slate-900 dark:text-slate-100">Almost there</h1>
            <p className="text-slate-600 dark:text-slate-300 mt-2">
              We haven't received confirmation from Stripe yet. Your card may still be processing —
              please refresh in a minute.
            </p>
            <Link to="/" className="inline-block mt-6 text-sm text-sky-700 dark:text-sky-300 hover:underline">← Back to the site</Link>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="w-12 h-12 mx-auto mb-4 text-red-600" />
            <h1 className="font-heading text-xl font-semibold text-slate-900 dark:text-slate-100">Couldn't verify tip</h1>
            <p className="text-slate-600 dark:text-slate-300 mt-2">
              We couldn't find that checkout session. If you were charged, please contact the site owner
              and we'll get it sorted.
            </p>
            <Link to="/" className="inline-block mt-6 text-sm text-sky-700 dark:text-sky-300 hover:underline">← Back to the site</Link>
          </>
        )}
      </div>
    </div>
  );
}
